import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useCallback, useEffect, useState } from 'react';
import type { DateRange } from 'react-day-picker';

interface RelocationsFiltersState {
  status: string;
  cart_id: string;
  created_at: DateRange | undefined;
}

interface UseRelocationsFiltersProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
}

function formatDateForAPI(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export const useRelocationsFilters = ({ initialConditions = [], onChange }: UseRelocationsFiltersProps) => {
  const [filters, setFilters] = useState<RelocationsFiltersState>(() => parseConditionsToState(initialConditions));

  const convertStateToConditions = useCallback((state: RelocationsFiltersState): FilterConditionOutput[] => {
    const conditions: FilterConditionOutput[] = [];

    if (state.status) {
      conditions.push({
        column: 'status',
        action: 'equals',
        value: state.status,
      });
    }

    if (state.cart_id) {
      conditions.push({
        column: 'cart_id',
        action: 'equals',
        value: state.cart_id,
      });
    }

    if (state.created_at?.from && state.created_at?.to) {
      conditions.push({
        column: 'created_at',
        action: 'greater_equal',
        value: formatDateForAPI(state.created_at.from),
      });
      conditions.push({
        column: 'created_at',
        action: 'less_equal',
        value: formatDateForAPI(state.created_at.to),
      });
    }

    return conditions;
  }, []);

  const updateFilters = useCallback(
    (updates: Partial<RelocationsFiltersState>) => {
      setFilters((prev) => {
        const newState = { ...prev, ...updates };
        onChange?.(convertStateToConditions(newState));
        return newState;
      });
    },
    [convertStateToConditions, onChange],
  );

  const handleStatusChange = useCallback((value: string) => updateFilters({ status: value }), [updateFilters]);
  const handleCartIdChange = useCallback((value: string) => updateFilters({ cart_id: value }), [updateFilters]);
  const handleCreatedAtChange = useCallback(
    (range: DateRange | undefined) => updateFilters({ created_at: range }),
    [updateFilters],
  );

  const clearAllFilters = useCallback(() => {
    const emptyState: RelocationsFiltersState = {
      status: '',
      cart_id: '',
      created_at: undefined,
    };
    setFilters(emptyState);
    onChange?.([]);
  }, [onChange]);

  useEffect(() => {
    setFilters(parseConditionsToState(initialConditions));
  }, [initialConditions]);

  return {
    filters,
    handleStatusChange,
    handleCartIdChange,
    handleCreatedAtChange,
    clearAllFilters,
  };
};

function parseConditionsToState(conditions: FilterConditionOutput[]): RelocationsFiltersState {
  const state: RelocationsFiltersState = {
    status: '',
    cart_id: '',
    created_at: undefined,
  };

  const byColumn: Record<string, FilterConditionOutput[]> = {};
  conditions.forEach((c) => {
    if (!byColumn[c.column]) byColumn[c.column] = [];
    byColumn[c.column].push(c);
  });

  const statusCond = byColumn['status']?.[0];
  if (statusCond?.value !== undefined && statusCond.value !== '') {
    state.status = Array.isArray(statusCond.value) ? String(statusCond.value[0]) : String(statusCond.value);
  }

  const cartCond = byColumn['cart_id']?.[0];
  if (cartCond?.value !== undefined && cartCond.value !== '') {
    state.cart_id = Array.isArray(cartCond.value) ? String(cartCond.value[0]) : String(cartCond.value);
  }

  const dateMin = byColumn['created_at']?.find((c) => c.action === 'greater_equal');
  const dateMax = byColumn['created_at']?.find((c) => c.action === 'less_equal');
  if (dateMin?.value && dateMax?.value) {
    state.created_at = {
      from: new Date(String(dateMin.value)),
      to: new Date(String(dateMax.value)),
    };
  }

  return state;
}
